import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faBars, faUser, faClipboardList, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { usePatientProfile } from "../../PatientProfileProvider";
import { Chrono } from "react-chrono";

interface Goal {
  description: string; // Example structure, replace with the actual property name
}


const InfoBar = () => {
  const { patientData, isInfoBarOpen, setIsInfoBarOpen } = usePatientProfile();

  const handleToggle = () => {
    setIsInfoBarOpen(!isInfoBarOpen);
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={handleToggle}
        className="fixed top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none z-20"
      >
        <FontAwesomeIcon icon={isInfoBarOpen ? faTimes : faBars} size="lg" />
      </button>

      {/* Dark Overlay */}
      {isInfoBarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-5"
          onClick={handleToggle}
        ></div>
      )}

      {/* InfoBar Panel */}
      <div
        className={`fixed top-0 right-0 z-10 bg-gradient-to-b from-gray-100 to-white dark:from-gray-800 dark:to-gray-900 shadow-2xl transition-transform duration-300 ${
          isInfoBarOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "80%", height: "100vh" }}
      >
        {/* Header */}
        <div className="flex justify-between items-center text-white px-6 py-4  shadow">
          <h2 className="text-xl font-semibold">Patient Details</h2>
        </div>

        {/* Scrollable Content */}
        <div className="px-6 py-4 space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 80px)" }}>
          {patientData ? (
            <div className="space-y-8">
              {/* Patient Info Section */}
              <div className="flex flex-col gap-4 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="text-white text-base" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                      {patientData.patient_name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">ID: {patientData.patient_id}</p>
                  </div>
                </div>
                <div className="text-md text-white dark:text-white">
                  Sessions: <span className="font-medium">{patientData.number_of_sessions}</span>
                </div>
              </div>

              {/* Summary Section */}
              <div className="bg-gray-100 dark:bg-gray-800 shadow-lg rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faClipboardList} className="text-white text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Summary</h3>
                </div>
                <p className="text-m text-white dark:text-white">{patientData.summary}</p>
              </div>

            {/* Goals Section */}
            <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faBullseye} className="text-white text-lg" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Goals</h3>
              </div>

              {/* Textual Goals */}
              {patientData.goals?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Goals:</h4>
                  <ul className="space-y-2">
                    {patientData.goals.map((goal: Goal, index: number) => (
                      <li key={index} className="flex items-start items-center gap-2 text-gray-800 dark:text-gray-200 ml-2">
                        <span className="text-orange-500">
                          <FontAwesomeIcon icon={faBullseye} size="sm" />
                        </span>
                        <p className="text-md font-medium">{goal.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Timeline Goals */}
              <div>
                <h4 className="text-md font-medium text-white dark:text-white mb-2">Timeline:</h4>
                <Chrono
                  key={JSON.stringify(patientData.formattedGoals)}
                  items={patientData.formattedGoals}
                  mode="VERTICAL"
                  disableToolbar
                  theme={{
                    titleColor: "orange",
                    titleColorActive: "white",
                    secondary: "orange"
                  }}
                />
              </div>
            </div>
            </div>
          ) : (
            <p className="text-center text-gray-600 dark:text-gray-300">No patient data available</p>
          )}
        </div>
      </div>
    </>
  );
};

export default InfoBar;
